﻿package org.zq.pixelset.platform

import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.DragEvent
import org.w3c.dom.events.Event
import org.w3c.files.File
import org.w3c.files.FileReader
import org.zq.pixelset.model.PickedImage
import kotlin.js.Promise

actual fun Modifier.onImageFileDrop(
    onDragActiveChange: (Boolean) -> Unit,
    onFilesDropped: (List<PickedImage>) -> Unit,
    onDropFailed: (() -> Unit)?,
): Modifier = composed {
    val dragCounter = remember { mutableIntStateOf(0) }
    DisposableEffect(onDragActiveChange, onFilesDropped) {
        fun setActive(active: Boolean) {
            onDragActiveChange(active)
        }

        val dragEnter: (Event) -> Unit = { event ->
            event.preventDefault()
            val dragEvent = event.unsafeCast<DragEvent>()
            if (hasImageItems(dragEvent)) {
                dragCounter.intValue++
                setActive(true)
            }
        }

        val dragOver: (Event) -> Unit = { event ->
            event.preventDefault()
            val dragEvent = event.unsafeCast<DragEvent>()
            if (hasImageItems(dragEvent)) {
                dragEvent.dataTransfer?.dropEffect = "copy"
            }
        }

        val dragLeave: (Event) -> Unit = { event ->
            event.preventDefault()
            dragCounter.intValue = (dragCounter.intValue - 1).coerceAtLeast(0)
            if (dragCounter.intValue == 0) setActive(false)
        }

        val drop: (Event) -> Unit = { event ->
            event.preventDefault()
            dragCounter.intValue = 0
            setActive(false)
            val dragEvent = event.unsafeCast<DragEvent>()
            val files = dragEvent.dataTransfer?.files
            if (files != null) {
                val imageFiles = (0 until files.length)
                    .mapNotNull { files.item(it) }
                    .filter { isImageMimeOrName(it) }
                if (imageFiles.isNotEmpty()) {
                    MainScope().launch {
                        val picked = readFiles(imageFiles)
                        if (picked.isNotEmpty()) {
                            onFilesDropped(picked)
                        } else {
                            onDropFailed?.invoke()
                        }
                    }
                }
            }
        }

        document.body?.let { body ->
            body.addEventListener("dragenter", dragEnter)
            body.addEventListener("dragover", dragOver)
            body.addEventListener("dragleave", dragLeave)
            body.addEventListener("drop", drop)
        }

        onDispose {
            document.body?.let { body ->
                body.removeEventListener("dragenter", dragEnter)
                body.removeEventListener("dragover", dragOver)
                body.removeEventListener("dragleave", dragLeave)
                body.removeEventListener("drop", drop)
            }
        }
    }
    this
}

private fun hasImageItems(event: DragEvent): Boolean {
    val types = event.dataTransfer?.types
    if (types != null) {
        for (i in 0 until types.length) {
            val type = types[i]?.toString() ?: continue
            if (type == "Files" || type.contains("image", ignoreCase = true)) return true
        }
    }
    return true
}

private fun isImageMimeOrName(file: File): Boolean {
    val mime = file.type
    if (mime.startsWith("image/")) return true
    val name = file.name.lowercase()
    return name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".png") ||
        name.endsWith(".webp") || name.endsWith(".gif") || name.endsWith(".bmp")
}

private suspend fun readFiles(files: List<File>): List<PickedImage> {
    val promises = files.map { file -> readFileAsPickedImage(file) }
    val results = Promise.all(promises.toTypedArray()).await()
    @Suppress("UNCHECKED_CAST")
    return (results as Array<PickedImage>).toList()
}

private fun readFileAsPickedImage(file: File): Promise<PickedImage> = Promise { resolve, reject ->
    val reader = FileReader()
    reader.onload = {
        val buffer = reader.result
        if (buffer is ArrayBuffer) {
            resolve(
                PickedImage(
                    name = file.name,
                    bytes = Uint8Array(buffer as ArrayBuffer).toByteArray(),
                    sizeBytes = file.size.toLong(),
                ),
            )
        } else {
            reject(Exception("读取文件失败"))
        }
    }
    reader.onerror = { reject(Exception("读取文件失败")) }
    reader.readAsArrayBuffer(file)
}

private fun Uint8Array.toByteArray(): ByteArray {
    val dynamic = asDynamic()
    return ByteArray(length) { (dynamic[it] as Number).toInt().toByte() }
}
